import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { createRoot } from "react-dom/client";
import './index.css';
import App from './App';
import MinWidth from './pages/MinWidth';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import store from './redux/Store';
import axios from 'axios';
import { appTheme, appAccessibilityTheme } from './AppTheme';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';
import { deDE } from '@mui/material/locale';
import { CssBaseline } from '@mui/material';
import { LicenseInfo } from '@mui/x-license';

// Services - bleiben eager weil sie global gebraucht werden
import { CreativeService } from './service/CreativeService';
import { OrderService } from './service/OrderService';
import { LoggingService } from './service/LoggingService';
import { AuthenticationService } from './service/AuthenticationService';
import { BusinesspartnerService } from './service/BusinesspartnerService';
import { CreditStatusGuard } from './service/CreditStatusGuard';

// Error Boundary für robustes Error-Handling
import ErrorBoundary from './components/ErrorBoundary';
// Dezente Loading-Anzeige für Routenwechsel (nicht Splash)
import RouteLoadingIndicator from './components/RouteLoadingIndicator';

// =============================================================================
// React 19: Lazy Loading mit Code Splitting
// Seiten werden erst geladen wenn sie benötigt werden
// =============================================================================

// Desktop Pages - Lazy loaded
const Templates = lazy(() => import('./pages/Templates'));
const Creative = lazy(() => import('./pages/Creative'));
const Offers = lazy(() => import('./pages/Offers'));
const Schedule = lazy(() => import('./pages/Schedule'));
const Registration = lazy(() => import('./pages/Registration'));
const Order = lazy(() => import('./pages/Order'));
const Customer = lazy(() => import('./pages/Customer'));

// Mobile Pages - Lazy loaded
const MarketsMobile = lazy(() => import('./m/pages/Markets'));
const TemplatesMobile = lazy(() => import('./m/pages/Templates'));
const CreativeMobile = lazy(() => import('./m/pages/Creative'));
const OffersMobile = lazy(() => import('./m/pages/Offers'));
const OrderMobile = lazy(() => import('./m/pages/Order'));
const PreviewMobile = lazy(() => import('./m/pages/Preview'));
const UpsellingMobile = lazy(() => import('./m/pages/Upselling'));
const EditionsMobile = lazy(() => import('./m/pages/Editions'));
const ScheduleMobile = lazy(() => import('./m/pages/Schedule'));
const RegisterMailMobile = lazy(() => import('./m/pages/RegisterMail'));
const RegisterContactMobile = lazy(() => import('./m/pages/RegisterContact'));
const RegisterPaymentMobile = lazy(() => import('./m/pages/RegisterPayment'));
const CustomerMobile = lazy(() => import('./m/pages/Customer'));
const OrdersMobile = lazy(() => import('./m/pages/Orders'));
const InvoicesMobile = lazy(() => import('./m/pages/Invoices'));
const ServicePackagesMobile = lazy(() => import('./m/pages/ServicePackages'));

// Accessible Pages - Lazy loaded
const MarketsAcc = lazy(() => import('./a/pages/Markets'));
const TemplatesAcc = lazy(() => import('./a/pages/Templates'));
const CreativeAcc = lazy(() => import('./a/pages/Creative'));
const OffersAcc = lazy(() => import('./a/pages/Offers'));
const ScheduleAcc = lazy(() => import('./a/pages/Schedule'));
const EditionsAcc = lazy(() => import('./a/pages/Editions'));
const OrderAcc = lazy(() => import('./a/pages/Order'));
const CustomerAcc = lazy(() => import('./a/pages/Customer'));
const RegisterMailAcc = lazy(() => import('./a/pages/RegisterMail'));
const RegisterContactAcc = lazy(() => import('./m/pages/RegisterContact'));
const RegisterPaymentAcc = lazy(() => import('./m/pages/RegisterPayment'));
const OrdersAcc = lazy(() => import('./a/pages/Orders'));
const InvoicesAcc = lazy(() => import('./a/pages/Invoices'));
const ServicePackagesAcc = lazy(() => import('./a/pages/ServicePackages'));

/*
 * Main Application Entry Point
 */
const main = async () => {
  const index = await axios.get('/webstore/service/index');
  const properties = await axios.get(index.data._links.properties.href);

  // WS-8390: maintenance-Page bei inaktivem WebStore
  if (index.data.tenant?.disabled && properties.data.maintenanceUrl) {
    let location = '';
    try {
      location = new URL(properties.data.maintenanceUrl);
    }
    catch (e) {
      location = '/webstore/' + properties.data.maintenanceUrl;
    }
    window.location = location;
    return;
  }

  const blockingChannel = new BroadcastChannel('blocking-channel');
  blockingChannel.onmessage = e => {
    if ('starting' === e.data) {
      blockingChannel.postMessage('rejected');
    }
    else if ('rejected' === e.data) {
      let bu = properties.data.blockingUrl;
      bu += "?org=" + encodeURIComponent(window.location.href);
      window.location = bu;
    }
  };
  blockingChannel.postMessage('starting');


  let l = deDE;

  let MUI_LICENSE_KEY = '6e2b4820f96397165c48e437871fb65aTz00NzY3MSxFPTE2ODk5NDQ0NDU0OTEsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI='
  LicenseInfo.setLicenseKey(MUI_LICENSE_KEY);

  const urlParams = new URLSearchParams(window.location.search);
  let theme = createTheme(urlParams.has('ac') ? appAccessibilityTheme(properties.data) : appTheme(properties.data), l);

  if (urlParams.has('ac')) {
    window.WS_VARIANT = 'a';
  }
  else if (urlParams.has('mobile')) {
    window.WS_VARIANT = 'm';
  }
  else {
    window.WS_VARIANT = 'd';
  }

  theme = responsiveFontSizes(theme);
  const container = document.getElementById('root');
  const root = createRoot(container);

  root.render(
    <React.StrictMode>
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {/* Error Boundary fängt alle unerwarteten Fehler ab */}
          <ErrorBoundary>
            {/* Services - werden sofort geladen */}
            <CreativeService />
            <OrderService />
            <LoggingService />
            <AuthenticationService />
            <BusinesspartnerService />
            {/* ASE-551/ZEN-30575: Hinweis bei gesperrtem Kunden - gilt fuer alle drei Oberflaechen */}
            <CreditStatusGuard />

            {/* Suspense Boundary für lazy-geladene Routen - dezente Loading-Anzeige */}
            <Suspense fallback={<RouteLoadingIndicator />}>
              <BrowserRouter>
                <Routes>
                  {/* Desktop Routes */}
                  <Route exact path="/" element={<App />} />
                  <Route exact path="/webstore/app/index.html" element={<App />} />
                  <Route exact path="/webstore/app/min-width" element={<MinWidth />} />
                  <Route exact path="/webstore/app/templates" element={<Templates />} />
                  <Route path="/webstore/app/creative" element={<Creative />} />
                  <Route exact path="/webstore/app/offers" element={<Offers />} />
                  <Route exact path="/webstore/app/schedule" element={<Schedule />} />
                  <Route path="/webstore/app/order" element={<Order />} />
                  <Route exact path="/webstore/app/customer" element={<Customer />} />
                  <Route path="/webstore/app/customer/:tab" element={<Customer />} />
                  <Route exact path="/webstore/app/register" element={<Registration />} />
                  <Route path="/webstore/app/register/:tab" element={<Registration />} />

                  {/* Mobile Routes */}
                  <Route path="/webstore/app/m/markets" element={<MarketsMobile />} />
                  <Route path="/webstore/app/m/templates" element={<TemplatesMobile />} />
                  <Route path="/webstore/app/m/creative" element={<CreativeMobile />} />
                  <Route path="/webstore/app/m/offers" element={<OffersMobile />} />
                  <Route path="/webstore/app/m/order" element={<OrderMobile />} />
                  <Route path="/webstore/app/m/preview" element={<PreviewMobile />} />
                  <Route path="/webstore/app/m/upselling" element={<UpsellingMobile />} />
                  <Route path="/webstore/app/m/editions" element={<EditionsMobile />} />
                  <Route path="/webstore/app/m/schedule" element={<ScheduleMobile />} />
                  <Route path="/webstore/app/m/register-mail" element={<RegisterMailMobile />} />
                  <Route path="/webstore/app/m/register-contact" element={<RegisterContactMobile />} />
                  <Route path="/webstore/app/m/register-payment" element={<RegisterPaymentMobile />} />
                  <Route path="/webstore/app/m/customer" element={<CustomerMobile />} />
                  <Route path="/webstore/app/m/customer-orders" element={<OrdersMobile />} />
                  <Route path="/webstore/app/m/customer-invoices" element={<InvoicesMobile />} />
                  <Route path="/webstore/app/m/customer-servicepackages" element={<ServicePackagesMobile />} />

                  {/* Accessible Routes */}
                  <Route path="/webstore/app/a/markets" element={<MarketsAcc />} />
                  <Route path="/webstore/app/a/templates" element={<TemplatesAcc />} />
                  <Route path="/webstore/app/a/creative" element={<CreativeAcc />} />
                  <Route path="/webstore/app/a/offers" element={<OffersAcc />} />
                  <Route path="/webstore/app/a/order" element={<OrderAcc />} />
                  <Route path="/webstore/app/a/preview" element={<PreviewMobile />} />
                  <Route path="/webstore/app/a/upselling" element={<UpsellingMobile />} />
                  <Route path="/webstore/app/a/editions" element={<EditionsAcc />} />
                  <Route path="/webstore/app/a/schedule" element={<ScheduleAcc />} />
                  <Route path="/webstore/app/a/register-mail" element={<RegisterMailAcc />} />
                  <Route path="/webstore/app/a/register-contact" element={<RegisterContactAcc />} />
                  <Route path="/webstore/app/a/register-payment" element={<RegisterPaymentAcc />} />
                  <Route path="/webstore/app/a/customer" element={<CustomerAcc />} />
                  <Route path="/webstore/app/a/customer-orders" element={<OrdersAcc />} />
                  <Route path="/webstore/app/a/customer-invoices" element={<InvoicesAcc />} />
                  <Route path="/webstore/app/a/customer-servicepackages" element={<ServicePackagesAcc />} />
                </Routes>
              </BrowserRouter>
            </Suspense>
          </ErrorBoundary>
        </ThemeProvider>
      </Provider>
    </React.StrictMode>
  );
};

main();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
